import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ResultsTable from "../components/ResultsTable";
import RoutingInsight from "../components/RoutingInsight";
import { getShareResult } from "../lib/api";
import { ProbeRun } from "../types";
import { Bone } from "../components/Skeleton";

export default function ShareResult() {
  const { runId } = useParams<{ runId: string }>();
  const { data: run, isLoading, error } = useQuery<ProbeRun>({
    queryKey: ["share", runId],
    queryFn: () => getShareResult(runId!),
    enabled: !!runId,
  });

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
          <div style={{ marginBottom: 24 }}>
            <Bone w={160} h={22} style={{ marginBottom: 10 }} />
            <Bone w={280} h={14} />
          </div>
          <div className="glass" style={{ display: "flex", gap: 32, marginBottom: 20, padding: "16px 24px" }}>
            {["P50", "P95", "P99"].map((label) => (
              <div key={label} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Bone w={28} h={11} />
                <Bone w={72} h={28} />
              </div>
            ))}
          </div>
          <div className="glass" style={{ padding: 24 }}>
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <Bone w={120} h={13} />
                <Bone w={56} h={13} />
                <Bone w={44} h={13} />
                <Bone w={44} h={13} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div style={centerStyle}>
        <p style={{ color: "#98A1AB" }}>Results not found or still processing.</p>
        <Link to="/" style={{ color: "#FF2566" }}>Run a new test →</Link>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#EFF2F5" }}>Probe Results</h1>
          <p style={{ margin: 0, color: "#98A1AB", wordBreak: "break-all" }}>{run.url}</p>
        </div>

        {run.percentiles && (
          <div className="glass" style={{ display: "flex", gap: 32, marginBottom: 20, padding: "16px 24px" }}>
            {([["P50", run.percentiles.p50], ["P95", run.percentiles.p95], ["P99", run.percentiles.p99]] as [string, number | null][]).map(([label, val]) => (
              <div key={label}>
                <p style={{ margin: 0, fontSize: 11, color: "#98A1AB", fontWeight: 700, letterSpacing: "0.05em" }}>{label}</p>
                <p style={{ margin: "3px 0 0", fontSize: 28, fontWeight: 800, color: "#EFF2F5" }}>
                  {val != null ? `${val}ms` : "—"}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="glass" style={{ padding: 24 }}>
          <ResultsTable results={run.results ?? []} regionCount={run.region_count} regionsDone={run.regions_done} />
          <RoutingInsight results={run.results ?? []} />
        </div>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link to="/" className="btn-primary" style={{ display: "inline-block", padding: "12px 28px", fontSize: 14, textDecoration: "none" }}>
            Test your own endpoint →
          </Link>
        </div>
      </div>
    </div>
  );
}

const centerStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", alignItems: "center",
  justifyContent: "center", height: "100vh", gap: 12,
  color: "#98A1AB",
};
